import React, { useCallback } from 'react';
import {
  khachHangSearchColumns,
  MA_CHUNG_TU,
  nhanVienSearchColumns,
  QUERY_KEYS,
  taiKhoanSearchColumns
} from '@/constants';
import {
  CurrencyInputV2,
  DocumentNumberFieldV2,
  FormFieldV2,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';

import { AccountModel, KhachHang, NhanVien, TaiKhoan } from '@/types/schemas';
import { useDetailSelection } from '@/hooks/use-detail-selection';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useHanThanhToan } from '@/hooks';

interface BasicInformationTabProps {
  formMode: 'add' | 'edit' | 'view';
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

const BasicInfoTab = ({ formMode, formState: { state, actions } }: BasicInformationTabProps) => {
  const { paymentTerms, isLoading } = useHanThanhToan();
  const handleAccountSelection = useDetailSelection<AccountModel>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    onSelect: actions.setTaiKhoan
  });
  const handleExchangeRateChange = useCallback(
    (exchangeRate: number | null) => {
      if (exchangeRate) {
        actions.setTyGia(exchangeRate);
      }
    },
    [actions]
  );
  const pt_tao_yn = state.pt_tao_yn;

  return (
    <div className='flex flex-col gap-3 p-4'>
      <div className='grid grid-cols-12 gap-6'>
        <div className='col-span-6 space-y-1'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center'>
              <Label className='w-32 min-w-32 text-sm font-medium'>Mã khách hàng</Label>
              <SearchField<KhachHang>
                type='text'
                columnDisplay='customer_code'
                searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}`}
                searchColumns={khachHangSearchColumns}
                value={state.khachHang?.customer_code || ''}
                onRowSelection={actions.setKhachHang}
                disabled={formMode === 'view'}
                dialogTitle='Danh mục khách hàng'
              />
            </div>

            <div className='flex items-center'>
              <Label className='w-24 min-w-24 text-sm font-medium'>Mã số thuế</Label>
              <FormFieldV2
                name='ma_so_thue'
                type='text'
                disabled={formMode === 'view'}
                placeholder='Nhập và tra cứu'
                value={state.khachHang?.tax_code || ''}
              />
            </div>
            <div className='flex items-center gap-2'>
              <div className='flex flex-shrink-0 items-center'>
                <Checkbox
                  name='pt_tao_yn'
                  id='pt_tao_yn'
                  disabled={formMode === 'view'}
                  onCheckedChange={actions.setPtTaoYn}
                  checked={pt_tao_yn}
                />
                {!pt_tao_yn && <Label className='ml-2'>Thu tiền</Label>}
              </div>
              {pt_tao_yn && (
                <div className='flex items-center gap-2'>
                  <FormFieldV2
                    name='ma_httt'
                    type='select'
                    disabled={formMode === 'view'}
                    className='w-[150px] flex-shrink-0'
                    options={[
                      { value: 'TMB', label: 'Tiền mặt' },
                      { value: 'CKB', label: 'Chuyển khoản' },
                      { value: 'KB', label: 'Khác' }
                    ]}
                    value={state.ma_httt}
                    onValueChange={actions.setMaHttt}
                  />
                </div>
              )}
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Tên khách hàng</Label>
            <div className='flex-1'>
              <FormFieldV2
                name='ten_kh_thue'
                type='text'
                disabled={formMode === 'view'}
                placeholder='Nhập tên khách hàng/đơn vị'
                value={state.khachHang?.customer_name || ''}
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Địa chỉ</Label>
            <div className='flex-1'>
              <FormFieldV2
                name='dia_chi'
                type='text'
                disabled={formMode === 'view'}
                placeholder='Nhập địa chỉ khách hàng'
                value={state.khachHang?.address || ''}
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Mã nhân viên</Label>
            <SearchField<NhanVien>
              type='text'
              displayRelatedField='ma_nhan_vien'
              columnDisplay='ma_nhan_vien'
              searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}`}
              searchColumns={nhanVienSearchColumns}
              value={
                state.nhanVien?.ma_nhan_vien ||
                state.khachHang?.sales_rep_data?.ma_nhan_vien ||
                state.nhanVien?.ma_nv ||
                ''
              }
              relatedFieldValue={
                state.nhanVien?.ho_ten_nhan_vien ||
                state.khachHang?.sales_rep_data?.ho_ten_nhan_vien ||
                state.nhanVien?.ten_nv ||
                ''
              }
              onRowSelection={actions.setNhanVien}
              disabled={formMode === 'view'}
              dialogTitle='Danh mục nhân viên bán hàng'
              className='w-100'
            />
          </div>

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Tài khoản nợ</Label>
            <SearchField<TaiKhoan>
              type='text'
              displayRelatedField='code'
              columnDisplay='code'
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}`}
              searchColumns={taiKhoanSearchColumns}
              value={state.taiKhoan?.code || state.khachHang?.account_data?.code || ''}
              relatedFieldValue={state.taiKhoan?.name || state.khachHang?.account_data?.name || ''}
              onRowSelection={(row: TaiKhoan | null) => handleAccountSelection(row as AccountModel | null)}
              disabled={formMode === 'view'}
              dialogTitle='Danh mục tài khoản'
              className='w-100'
            />
          </div>

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Diễn giải</Label>
            <div className='flex-1'>
              <FormFieldV2
                name='dien_giai'
                type='text'
                disabled={formMode === 'view'}
                value={state.dienGiai || ''}
                onValueChange={actions.setDienGiai}
              />
            </div>
          </div>
        </div>

        <div className='col-span-3 space-y-1'>
          <div className='ml-4 flex h-8 items-center'></div>

          <div className='flex items-center gap-3'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Dư công nợ</Label>
            <Label className='font-medium text-red-500'>0</Label>
          </div>

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Người mua hàng</Label>
            <div className='flex-1'>
              <FormFieldV2
                name='ong_ba'
                type='text'
                disabled={formMode === 'view'}
                placeholder='Người mua hàng'
                value={state.khachHang?.contact_person || ''}
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Email</Label>
            <div className='flex-1'>
              <FormFieldV2
                name='e_mail'
                type='text'
                disabled={formMode === 'view'}
                placeholder='Nhập email'
                value={state.khachHang?.email || ''}
              />
            </div>
          </div>

          {!state.pt_tao_yn && (
            <div className='flex items-center'>
              <Label className='w-32 min-w-32 text-sm font-medium'>Hạn thanh toán</Label>
              <div className='flex-1'>
                {!isLoading && (
                  <FormFieldV2
                    name='ma_tt'
                    type='select'
                    options={paymentTerms.map(term => ({ value: term.uuid, label: term.ten_tt }))}
                    disabled={formMode === 'view'}
                    className='w-80'
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div className='col-span-3 space-y-1'>
          <DocumentNumberFieldV2
            ma_ct={MA_CHUNG_TU.BAN_HANG.HOA_DON_BAN_DICH_VU}
            quyenChungTu={state.quyenChungTu}
            onQuyenChungTuChange={actions.setQuyenChungTu}
            soChungTu={state.soChungTuHienTai}
            onSoChungTuChange={actions.setSoChungTuHienTai}
            formMode={formMode || 'add'}
            classNameSearchField='w-full'
            labelClassName='font-medium w-32 min-w-32'
          />

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Ngày chứng từ</Label>
            <div className='flex-1'>
              <FormFieldV2 name='ngay_ct' type='date' disabled={formMode === 'view'} className='w-80' />
            </div>
          </div>

          <CurrencyInputV2
            formMode={formMode}
            onExchangeRateChange={handleExchangeRateChange}
            className='flex items-center gap-4'
            classNameInput='min-w-0 flex-1'
            labelClassName='w-[100px] shrink-0 text-[13px] xl:w-[105px] 2xl:w-28 font-medium text-sm'
          />
          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'>Trạng thái</Label>
            <div className='flex-1'>
              <FormFieldV2
                name='status'
                type='select'
                disabled={formMode === 'view'}
                options={[
                  { value: '0', label: 'Chưa ghi sổ' },
                  { value: '3', label: 'Đã ghi sổ' },
                  { value: '5', label: 'Chờ duyệt' },
                  { value: '9', label: 'Hủy' }
                ]}
                className='w-32'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-32 min-w-32 text-sm font-medium'></Label>
            <Checkbox id='recordedData' name='transfer_yn' disabled />
            <Label className='ml-2 text-sm font-medium' htmlFor='recordedData'>
              Dữ liệu được ghi nhận
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoTab;
